import React, { useEffect, useRef } from 'react';
import { highlightText } from '../utils/highlightText';

const VerseHighlighter = ({
  verseText,
  verseId,
  book,
  chapter,
  highlights = [],
  handleVerseClick,
  hasNote,
  hasSermon,
  columnId,
  verseNumber, 
  version,
  highlightColor = 'lightblue',
  onNoteClick,
  onSermonClick,
  onHighlightClick,
  onUnmappedHighlightClick,
  onFootnoteMarkerClick,
  isMenuActive = false,
  children,
  // Pass the verse number using the index
}) => {
  const containerRef = useRef(null);
  const verseTextRef = useRef(null);

  // Click handler for both notes and sermons
  const handleClick = (e) => {
    const footnoteTarget = e.target?.closest?.('[data-footnote-marker]');
    if (footnoteTarget && onFootnoteMarkerClick) {
      onFootnoteMarkerClick(footnoteTarget.getAttribute('data-footnote-marker'), e);
      return;
    }

    const highlightTarget = e.target?.closest?.('[data-highlight-id]');
    if (highlightTarget && onHighlightClick) {
      const highlight = highlights.find(
        (item) => String(item.id) === highlightTarget.getAttribute('data-highlight-id')
      );
      if (highlight) {
        onHighlightClick(e, highlight, verseId, book, chapter, version, columnId, verseTextRef.current);
        return;
      }
    }

    const selection = window.getSelection();
    const verseElement = verseTextRef.current;
    const hasActiveVerseSelection = (
      selection &&
      selection.rangeCount > 0 &&
      !selection.isCollapsed &&
      verseElement &&
      verseElement.contains(selection.anchorNode)
    );

    // Drag-select is handled by the document mouseup listener below.
    // Avoid overwriting that partial selection with a click fallback.
    if (hasActiveVerseSelection) {
      return;
    }

    if (handleVerseClick) {
      handleVerseClick(e, verseId, book, chapter, version, columnId, verseTextRef.current);

    } else {
      alert('Please log in to use this feature.');
    }
  };

  useEffect(() => {
    if (!handleVerseClick) return undefined;
    let dragStartedInVerse = false;
    const onDocMouseDown = (event) => {
      const verseEl = verseTextRef.current;
      dragStartedInVerse = Boolean(verseEl && event.target instanceof Node && verseEl.contains(event.target));
    };
    const onDocMouseUp = (event) => {
      const started = dragStartedInVerse;
      dragStartedInVerse = false;
      if (event.button !== 0) return;
      // Only react to drag-ends that began on THIS verse's text — otherwise
      // clicking a popup button (while a selection still exists) would
      // re-trigger handleVerseClick and reset the menu state.
      if (!started) return;
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return;
      const verseEl = verseTextRef.current;
      if (!verseEl || !selection.anchorNode || !verseEl.contains(selection.anchorNode)) return;
      handleVerseClick(event, verseId, book, chapter, version, columnId, verseEl);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('mouseup', onDocMouseUp);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('mouseup', onDocMouseUp);
    };
  }, [handleVerseClick, verseId, book, chapter, version, columnId]);
  
  return (
    <div
      className="verse-container"
      ref={containerRef}
      style={{ display: 'inline-block' }} // Ensure it's inline with the verse text
    >
      {/* Verse number at the start of the verse */}
      <span className="verse-number  mr-1">
        {verseNumber}.
      </span>

      {/* Verse text with hover effect applied */}
      <span
        className={`cursor-pointer verse-text ${isMenuActive ? 'verse-text-active' : ''}`}
        ref={verseTextRef}
        onClick={handleClick}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleClick(event);
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={`Verse ${verseNumber}`}
        style={isMenuActive ? { backgroundColor: 'rgb(186, 186, 186)' } : undefined}
      >
        {children || (
          <span
            dangerouslySetInnerHTML={{ __html: highlightText(verseText, highlights, highlightColor) }}
          />
        )}
      </span>

      {highlights.some((highlight) => highlight.is_unmapped) && (
        <button
          type="button"
          className="ml-2 inline-flex h-6 w-6 items-center justify-center rounded-full border border-gray-500 text-sm text-gray-700 hover:bg-gray-100"
          title="This highlight is not mapped in this translation"
          aria-label="Open unmapped highlight"
          onClick={(event) => {
            event.stopPropagation();
            onUnmappedHighlightClick?.(event, highlights.find((highlight) => highlight.is_unmapped));
          }}
        >
          ◇
        </button>
      )}
      
      {/* Conditionally render the note icon if the verse has a note */}
      {hasNote && (
        <button
          type="button"
          onClick={() => onNoteClick(hasNote.id)}
          className="note-icon cursor-pointer hover:bg-gray-200 ml-2" // Hover effect on note icon only
          aria-label="Open memo"
        >
          📝
        </button>
      )}

      {/* Conditionally render the sermon icon if the verse has a sermon */}
      {hasSermon && (
        <button
          type="button"
          onClick={()=>onSermonClick(hasSermon.id)}
          className="sermon-icon cursor-pointer hover:bg-yellow-200 ml-2" // Hover effect on sermon icon only
          aria-label="Open sermon"
        >
          📖
        </button>
      )}
    </div>
  );
};

export default VerseHighlighter;
